import { useDataTableV2ConfigElement } from '../../hooks/useDataTableV2Config.js';
import {
  type Column,
  type Header,
  type InitialTableState,
  type RowData,
  type Table,
  type TableFeature,
  type TableOptions,
} from '../hooks/useTable/types.js';

/**
 * Options for DataTableV2 visual representation
 * @public
 */
export interface DataTableV2VisualVariantProps {
  variant?: {
    /**
     * rowSeparation can be set to 'horizontalDividers' which will provide lines between rows or
     * can be set to 'zebraStripes' which will provide alternate row coloring.
     * @defaultValue 'horizontalDividers'
     */
    rowSeparation?: 'none' | 'horizontalDividers' | 'zebraStripes';

    /**
     * If true provides vertical lines between the columns.
     * @defaultValue false
     */
    verticalDividers?: boolean;

    /**
     * If true provides a border for the table.
     * @defaultValue true
     */
    contained?: boolean;

    /**
     * rowDensity adds spacing around the content inside the row
     * with minimal space for 'condensed', maximum spacing for 'comfortable' and
     * 'default' being medium spacing.
     * @defaultValue 'default'
     */
    rowDensity?: 'default' | 'condensed' | 'comfortable';

    /**
     * fontStyle, sets the font style for the entire data table.
     * This does not overwrite any fontStyle set at the lower level, like columns etc.
     * @defaultValue 'text'
     */
    fontStyle?: 'text' | 'code';

    /**
     * headers defines if the header rows of the table should be rendered or not.
     * @defaultValue 'default'
     */
    headers?: 'default' | 'hidden';

    /**
     * Determines the vertical alignment for all table cells.
     * If header and body cells should have a different vertical alignment, they can be configured separately.
     * @defaultValue 'top'
     */
    verticalAlignment?:
      | 'top'
      | 'center'
      | 'bottom'
      | {
          header: 'top' | 'center' | 'bottom';
          body: 'top' | 'center' | 'bottom';
        };
  };
}

// Extension interface to extend the tanstack table state with the visualVariant props
export interface DataTableV2VisualVariantTableState {
  // As all values will be defaulted to not be undefined within the setup,
  // we can resolve typescript here for object and all keys to be required and not undefined.
  visualVariantConfig: Required<
    Required<DataTableV2VisualVariantProps>['variant']
  >;
}

export interface DataTableV2VisualVariantTableInstance {
  getVisualVariantOptions: () => DataTableV2VisualVariantTableState['visualVariantConfig'];
}

export interface DataTableV2VisualVariantCell {
  getVisualVariantOptions: () => DataTableV2VisualVariantTableState['visualVariantConfig'];
}

/**
 * Extension interface to extend the types of the `Column` from the tanstack table.
 * @internal
 */
export interface DataTableV2VisualVariantColumn {
  /**
   * Gets the vertical alignment of a cell in the table body from the visual config.
   */
  getVerticalAlignment: () => 'top' | 'center' | 'bottom';
}

/**
 * Extension interface to extend the types of the `HeaderCell` from the tanstack table.
 * @internal
 */
export interface DataTableV2VisualVariantHeader {
  /**
   * Gets the vertical alignment of a cell in the table header from the visual config.
   */
  getVerticalAlignment: () => 'top' | 'center' | 'bottom';
}

export const DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT = {
  rowSeparation: 'horizontalDividers',
  verticalDividers: false,
  contained: true,
  rowDensity: 'default',
  fontStyle: 'text',
  headers: 'default',
  verticalAlignment: 'top',
} as const;

/**
 * Typeguard function to make sure a passed vertical alignment is an extended config.
 * @internal
 */
function isVerticalAlignmentExtendedConfig(
  verticalAlignment?:
    | 'top'
    | 'center'
    | 'bottom'
    | {
        header: 'top' | 'center' | 'bottom';
        body: 'top' | 'center' | 'bottom';
      },
): verticalAlignment is {
  header: 'top' | 'center' | 'bottom';
  body: 'top' | 'center' | 'bottom';
} {
  if (
    verticalAlignment !== undefined &&
    typeof verticalAlignment !== 'string'
  ) {
    return true;
  }

  return false;
}

export function useVisualVariant<T>(
  props: DataTableV2VisualVariantProps,
  options: TableOptions<T>,
) {
  const configVariant = useDataTableV2ConfigElement('variant');

  options.state = {
    ...options.state,
    visualVariantConfig: {
      rowSeparation:
        props.variant?.rowSeparation ??
        configVariant?.rowSeparation ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.rowSeparation,
      verticalDividers:
        props.variant?.verticalDividers ??
        configVariant?.verticalDividers ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.verticalDividers,
      contained:
        props.variant?.contained ??
        configVariant?.contained ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.contained,
      rowDensity:
        props.variant?.rowDensity ??
        configVariant?.rowDensity ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.rowDensity,
      fontStyle:
        props.variant?.fontStyle ??
        configVariant?.fontStyle ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.fontStyle,
      headers:
        props.variant?.headers ??
        configVariant?.headers ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.headers,
      verticalAlignment:
        props.variant?.verticalAlignment ??
        configVariant?.verticalAlignment ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.verticalAlignment,
    },
  };
}

export const VisualVariantConfig: TableFeature = {
  getInitialState(state?: InitialTableState) {
    return {
      visualVariantConfig: {
        rowSeparation: DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.rowSeparation,
        verticalDividers:
          DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.verticalDividers,
        contained: DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.contained,
        rowDensity: DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.rowDensity,
        fontStyle: DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.fontStyle,
        headers: DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.headers,
        verticalAlignment:
          DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.verticalAlignment,
      },
      ...state,
    };
  },

  createTable<TData extends RowData>(table: Table<TData>): void {
    table.getVisualVariantOptions = () => {
      return table.getState().visualVariantConfig;
    };
  },

  createColumn<TData extends RowData, TValue>(
    column: Column<TData, TValue>,
    table: Table<TData>,
  ) {
    column.getVerticalAlignment = () => {
      const verticalAlignment =
        table.getVisualVariantOptions().verticalAlignment;

      if (isVerticalAlignmentExtendedConfig(verticalAlignment)) {
        return verticalAlignment['body'];
      }

      return verticalAlignment;
    };
  },

  createHeader<TData extends RowData, TValue>(
    header: Header<TData, TValue>,
    table: Table<TData>,
  ) {
    header.getVerticalAlignment = () => {
      const verticalAlignment =
        table.getVisualVariantOptions().verticalAlignment;

      if (isVerticalAlignmentExtendedConfig(verticalAlignment)) {
        return verticalAlignment['header'];
      }

      return verticalAlignment;
    };
  },
};
